import React, { useCallback, useEffect, useState } from 'react';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { useInjection } from 'services/ioc/ioc.react';
import { ServiceTypes } from 'constants/ServiceTypes';
import update, { Spec } from 'immutability-helper';
import { BaseState } from 'models/baseRunners/BaseState';
import { PlayerName } from 'models/baseRunners/PlayerName';
import { LastDroppedPlayer } from 'models/baseRunners/LastDropperPlayer';
import { BaseRunners } from 'models/baseRunners/BaseRunners';
import { Play } from 'models/tagging/Play';
import BackgroundImage from '../CatcherThrowsFlow/images/CatcherThrows.png';
import Base from './Base';
import RemovePlayer from './RemovePlayer/RemovePlayer';
import BaseRoster from './BaseRoster';
import { AllPositions } from './AllPositions';
import { BasePositions } from './BasePositions';
import EndSequence from './EndSequence/EndSequence';
import UndoRemovePlayer from './RemovePlayer/UndoRemovePlayer';
import { EditChip } from '../EditChip/EditChip';
import { BaserunnersFlowWrapper, EditChipWrapper, ThirdRow } from './BaseRunnersFlowStyles';

interface IProps {
  exitBaseRunners: () => void;
  saveBaseRunners: (bases: BaseState[]) => void;
  baseRunners: BaseRunners;
  review: boolean;
  changedManually?: boolean;
  selectedPlay: Play;
}

const BaseRunnersFlow: React.FC<IProps> = (props): JSX.Element => {
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const [showBaseRoster, setShowBaseRoster] = useState<boolean>(false);
  const [selectedBase, setSelectedBase] = useState<number | null>(1);
  const [removedPlayer, setRemovedPlayer] = useState<BaseState | null>(null);
  const { exitBaseRunners, saveBaseRunners, baseRunners, changedManually, review, selectedPlay } = props;
  const [bases, setBases] = useState<Spec<BaseState[], any>>([]);
  const [isChanged, setIsChanged] = useState(false);
  const [removeBase] = useState<BaseState>({
    id: 'removeBase',
    accepts: BasePositions,
    lastDroppedItem: null,
  });

  useEffect(() => {
    return () => {
      setIsChanged(false);
    };
  }, []);

  useEffect(() => {
    setBases([
      {
        id: 3,
        accepts: AllPositions,
        lastDroppedItem: baseRunners.thirdBase
          ? { id: baseRunners.thirdBase?.id, name: baseRunners.thirdBase?.nameRef, type: baseRunners.thirdBase?.position }
          : null,
      },
      {
        id: 2,
        accepts: AllPositions,
        lastDroppedItem: baseRunners.secondBase
          ? { id: baseRunners.secondBase?.id, name: baseRunners.secondBase?.nameRef, type: baseRunners.secondBase?.position }
          : null,
      },
      {
        id: 1,
        accepts: AllPositions,
        lastDroppedItem: baseRunners.firstBase
          ? { id: baseRunners.firstBase?.id, name: baseRunners.firstBase?.nameRef, type: baseRunners.firstBase?.position }
          : null,
      },
    ]);
  }, [baseRunners]);

  const saveBaseRunnersHandler = () => {
    saveBaseRunners(bases);
  };

  const handleBaseChange = (baseIndex: number, player: PlayerName | LastDroppedPlayer | null = null): void => {
    setBases(
      update(bases, {
        [baseIndex]: {
          lastDroppedItem: {
            $set: player,
          },
        },
      })
    );
    setIsChanged(true);
  };

  const handlePlayerReplace = (
    removePlayerBaseIndex: number,
    targetBaseIndex: number,
    player: PlayerName | LastDroppedPlayer | null = null
  ): void => {
    setBases(
      update(bases, {
        [removePlayerBaseIndex]: {
          lastDroppedItem: {
            $set: null,
          },
        },
        [targetBaseIndex]: {
          lastDroppedItem: {
            $set: player,
          },
        },
      })
    );
    setIsChanged(true);
  };

  const handleDrop = useCallback(
    (index: number, item: PlayerName) => {
      const playerOnBase = bases.find((base: BaseState) => base.lastDroppedItem?.name === item.name);
      const targetBase = bases.find((base: BaseState, baseIndex: number) => baseIndex === index);

      if (playerOnBase !== null) {
        if (targetBase.lastDroppedItem === null) {
          handlePlayerReplace(bases.indexOf(playerOnBase), index, item);
        }
      } else {
        handleBaseChange(index, item);
      }
      setIsChanged(true);
    },
    [bases]
  );

  const removePlayer = (item: PlayerName): void => {
    const playerOnBase = bases.find((base: BaseState) => base.lastDroppedItem?.name === item.name);

    if (playerOnBase) {
      handleBaseChange(bases.indexOf(playerOnBase), null);
      setIsChanged(true);
    }
  };

  const addPlayerFromRoster = (player: any): void => {
    if (selectedBase !== null) {
      handleBaseChange(selectedBase, { id: player.id, name: player.nameRef, type: player.position });
    }

    setShowBaseRoster(false);
    setSelectedBase(null);
    setIsChanged(true);
  };

  const undoRemove = (player: BaseState): void => {
    const lastBase = bases.find((base: BaseState) => base.id === player.id);

    if (lastBase !== null) {
      handleBaseChange(bases.indexOf(lastBase), player.lastDroppedItem);
      setRemovedPlayer(null);
      setIsChanged(true);
    }
  };

  const emptyBaseClickHandler = (index: number): void => {
    setShowBaseRoster(true);
    setSelectedBase(index);
  };

  const [shouldRenderRemovePlayer, setShouldRenderRemovePlayer] = useState(false);

  const onRemoveHandler = (item: PlayerName): void => {
    const playerOnBase = bases.find((base: BaseState) => base.lastDroppedItem?.name === item.name);
    setRemovedPlayer(playerOnBase);
    removePlayer(item);
    setIsChanged(true);
    setShouldRenderRemovePlayer(false);
  };

  const draggingHandler = (isDragging: boolean) => {
    setShouldRenderRemovePlayer(isDragging);
  };

  return (
    <>
      <BaserunnersFlowWrapper style={{ backgroundImage: `url(${BackgroundImage})`, minHeight: review ? '440px' : 'unset' }}>
        <EditChipWrapper>{changedManually && <EditChip />}</EditChipWrapper>
        <div style={{ overflow: 'hidden', clear: 'both' }}>
          <ThirdRow>
            {bases.map((base: BaseState, index: number) => {
              const { id, accepts, lastDroppedItem } = base;
              return (
                <Base
                  accept={accepts}
                  lastDroppedItem={lastDroppedItem}
                  onDrop={(item: PlayerName): void => handleDrop(index, item)}
                  key={id}
                  id={id}
                  onEmptyClick={(): void => emptyBaseClickHandler(index)}
                  draggingHandler={draggingHandler}
                />
              );
            })}
          </ThirdRow>
        </div>
        {shouldRenderRemovePlayer && <RemovePlayer accept={removeBase.accepts} onDrop={onRemoveHandler} key={0} />}
        {!shouldRenderRemovePlayer && removedPlayer && (
          <UndoRemovePlayer onPressHotkeyCallback={undoRemove} lastDroppedItem={removedPlayer as any} />
        )}

        {showBaseRoster && (
          <BaseRoster
            onClose={(): void => setShowBaseRoster(false)}
            players={
              review ? selectedPlay.getBattingTeamLineup().players : taggingStore.playStore.selectedPlay!.getBattingTeamLineup().players
            }
            onPlayerClick={addPlayerFromRoster}
            unavailablePlayers={bases.map((base: BaseState) => base.lastDroppedItem?.id || base.lastDroppedItem?.name)}
          />
        )}
      </BaserunnersFlowWrapper>
      <EndSequence onExitCallback={exitBaseRunners} onSaveCallback={saveBaseRunnersHandler} isChanged={isChanged} />
    </>
  );
};

export default BaseRunnersFlow;
